/**
 * Client Portal Components
 * 
 * READ-ONLY PORTAL - NON-NEGOTIABLE:
 * - No editing capability
 * - No messaging
 * - No cost/margin visibility (unit costs hidden, totals only)
 * - Simplified timeline (trades + milestones, no internal details)
 * - Token-based access
 * - Calm, professional, dispute-resistant
 */
import React, { useState, useEffect } from 'react';
import {
  Building2,
  Calendar,
  CheckCircle,
  FileText,
  Receipt,
  Loader2,
  AlertCircle,
  Shield,
  Truck,
  ClipboardCheck,
  Flag,
  DollarSign,
  Wrench
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ HELPERS ============

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0);
}

// Milestone icons
const MILESTONE_ICONS = {
  'MILESTONE': Flag,
  'DELIVERY': Truck,
  'INSPECTION': ClipboardCheck
};

// ============ CLIENT PORTAL LAYOUT ============

export function ClientPortalLayout({ children, clientName, orgName }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
    }}>
      {/* Header */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid #e2e8f0',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Building2 size={24} style={{ color: '#6366f1' }} />
            <div>
              <div style={{ 
                fontSize: '1.125rem', 
                fontWeight: '600', 
                color: '#1e293b' 
              }}>
                {orgName || 'Your Contractor'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                Client Portal
              </div>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            color: '#64748b',
            fontSize: '0.875rem'
          }}>
            <Shield size={14} />
            <span>Secure Portal for {clientName}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px 24px'
      }}>
        {children}
      </main>

      {/* Footer */}
      <footer style={{
        padding: '24px',
        textAlign: 'center',
        color: '#94a3b8',
        fontSize: '0.75rem',
        borderTop: '1px solid #e2e8f0',
        background: 'white',
        marginTop: '48px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <Shield size={12} />
          <span>This is a read-only portal. For questions, please contact your contractor directly.</span>
        </div>
      </footer>
    </div>
  );
}

// ============ CLIENT PORTAL PAGE ============

export function ClientPortalPage({ token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessData, setAccessData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [view, setView] = useState('overview'); // overview, timeline, invoices, variations

  // Validate token and get overview
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Validate token
        const validateRes = await fetch(`${API_URL}/api/client-portal/view/${token}/validate`);
        if (!validateRes.ok) {
          throw new Error('This link has expired or is invalid. Please contact your contractor for a new link.');
        }
        const accessInfo = await validateRes.json();
        setAccessData(accessInfo);
        
        // Get overview
        const overviewRes = await fetch(`${API_URL}/api/client-portal/view/${token}/overview`);
        if (!overviewRes.ok) {
          throw new Error('Failed to load your projects');
        }
        const overviewData = await overviewRes.json();
        setOverview(overviewData);
        
        // Select first job by default if available
        if (overviewData.jobs?.length > 0) {
          setSelectedJob(overviewData.jobs[0]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <ClientPortalLayout clientName="Loading..." orgName="Loading...">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px',
          gap: '16px'
        }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
          <span style={{ color: '#64748b' }}>Loading your portal...</span>
        </div>
      </ClientPortalLayout>
    );
  }

  if (error) {
    return (
      <ClientPortalLayout clientName="Error" orgName="">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px',
          gap: '16px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <AlertCircle size={48} style={{ color: '#f59e0b' }} />
          <h2 style={{ color: '#1e293b', margin: 0 }}>Unable to Access Portal</h2>
          <p style={{ color: '#64748b', textAlign: 'center', maxWidth: '400px' }}>
            {error}
          </p>
        </div>
      </ClientPortalLayout>
    );
  }

  return (
    <ClientPortalLayout clientName={accessData?.client_name} orgName={overview?.org_name}>
      {/* Welcome Section */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '1.5rem', 
          color: '#1e293b' 
        }}>
          Welcome, {accessData?.client_name}
        </h1>
        <p style={{ margin: 0, color: '#64748b' }}>
          Here you can view your project progress, invoices, and any approved changes.
        </p>
      </div>

      {/* Jobs List */}
      {overview?.jobs?.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '1.125rem', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Building2 size={20} style={{ color: '#6366f1' }} />
            Your Projects
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {overview.jobs.map(job => (
              <div
                key={job.id}
                onClick={() => {
                  setSelectedJob(job);
                  setView('timeline');
                }}
                style={{
                  padding: '16px',
                  border: selectedJob?.id === job.id ? '2px solid #6366f1' : '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: selectedJob?.id === job.id ? '#f8faff' : 'white',
                  transition: 'all 0.2s'
                }}
                data-testid={`job-card-${job.id}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                      {job.title}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      {job.address || 'Address not specified'}
                    </div>
                  </div>
                  <div style={{
                    padding: '4px 10px',
                    background: job.status === 'completed' ? '#dcfce7' : '#e0e7ff',
                    color: job.status === 'completed' ? '#166534' : '#4338ca',
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    {job.status_display}
                  </div>
                </div>
                
                {/* Progress Bar */}
                {job.milestones_total > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      color: '#64748b',
                      marginBottom: '4px'
                    }}>
                      <span>Progress</span>
                      <span>{job.milestones_completed} of {job.milestones_total} milestones</span>
                    </div>
                    <div style={{
                      height: '6px',
                      background: '#e2e8f0',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${(job.milestones_completed / job.milestones_total) * 100}%`,
                        background: '#6366f1',
                        borderRadius: '3px',
                        transition: 'width 0.3s'
                      }} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      {selectedJob && (
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setView('timeline')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: view === 'timeline' ? '#6366f1' : 'white',
              color: view === 'timeline' ? 'white' : '#64748b',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            data-testid="tab-timeline"
          >
            <Calendar size={16} />
            Timeline
          </button>
          <button
            onClick={() => setView('invoices')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: view === 'invoices' ? '#6366f1' : 'white',
              color: view === 'invoices' ? 'white' : '#64748b',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            data-testid="tab-invoices"
          >
            <Receipt size={16} />
            Invoices
          </button>
          <button
            onClick={() => setView('variations')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: view === 'variations' ? '#6366f1' : 'white',
              color: view === 'variations' ? 'white' : '#64748b',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            data-testid="tab-variations"
          >
            <FileText size={16} />
            Variations
          </button>
        </div>
      )}

      {/* Content Views */}
      {selectedJob && view === 'timeline' && (
        <ClientTimelineSection token={token} jobId={selectedJob.id} />
      )}
      
      {selectedJob && view === 'invoices' && (
        <ClientInvoicesSection token={token} jobId={selectedJob.id} />
      )}
      
      {selectedJob && view === 'variations' && (
        <ClientVariationsSection token={token} jobId={selectedJob.id} />
      )}

      {/* Outstanding Balance Alert (calm, not alarming) */}
      {overview?.total_outstanding > 0 && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fcd34d',
          borderRadius: '12px',
          padding: '16px 24px',
          marginTop: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <DollarSign size={20} style={{ color: '#d97706' }} />
          <div>
            <div style={{ fontWeight: '600', color: '#92400e' }}>
              Outstanding Balance: {formatCurrency(overview.total_outstanding)}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#a16207' }}>
              Please see the Invoices tab for payment details.
            </div>
          </div>
        </div>
      )}
    </ClientPortalLayout>
  );
}

// ============ TIMELINE SECTION (Simplified Gantt-Style) ============

function ClientTimelineSection({ token, jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/client-portal/view/${token}/job/${jobId}/timeline`);
        if (res.ok) {
          setData(await res.json());
        }
      } catch (err) {
        console.error('Error fetching timeline:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, jobId]);

  if (loading) {
    return (
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '48px', 
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
      </div>
    );
  }

  if (!data) return null;

  // Trade colors (calm, professional)
  const tradeColors = {
    'Plumber': '#3b82f6',
    'Electrician': '#f59e0b',
    'Tiler': '#8b5cf6',
    'Carpenter': '#10b981',
    'Plasterer': '#ec4899',
    'Decorator': '#06b6d4',
    'default': '#6366f1'
  };

  const getTradeColor = (trade) => tradeColors[trade] || tradeColors.default;

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }} data-testid="timeline-section">
      <h3 style={{ 
        margin: '0 0 8px 0', 
        fontSize: '1.125rem', 
        color: '#1e293b' 
      }}>
        {data.job_title}
      </h3>
      
      {/* Progress Overview */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: `conic-gradient(#6366f1 ${data.progress_percentage * 3.6}deg, #e2e8f0 0deg)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '700',
            color: '#6366f1'
          }}>
            {Math.round(data.progress_percentage)}%
          </div>
        </div>
        <div>
          <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '1.125rem' }}>
            {data.current_phase}
          </div>
          <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
            {data.completed_items} of {data.total_items} items completed
          </div>
        </div>
      </div>

      {/* Trades Section (Gantt-style bars) */}
      {data.trades?.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ 
            margin: '0 0 12px 0', 
            fontSize: '0.875rem', 
            color: '#64748b',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Trades Attending
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {data.trades.map(trade => {
              const color = getTradeColor(trade.trade);
              const isCompleted = trade.is_completed;
              
              return (
                <div
                  key={trade.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: isCompleted ? '#f0fdf4' : 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${isCompleted ? '#22c55e' : color}`
                  }}
                  data-testid={`trade-${trade.id}`}
                >
                  {/* Trade Icon/Avatar */}
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: isCompleted ? '#22c55e' : color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {isCompleted ? (
                      <CheckCircle size={18} style={{ color: 'white' }} />
                    ) : (
                      <span style={{ color: 'white', fontWeight: '600', fontSize: '0.75rem' }}>
                        {(trade.trade || trade.title).charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  
                  {/* Trade Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: '500', 
                      color: isCompleted ? '#166534' : '#1e293b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {trade.trade || trade.title}
                      {trade.title !== trade.trade && trade.trade && (
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#64748b',
                          fontWeight: '400'
                        }}>
                          - {trade.title}
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: '0.8125rem', 
                      color: '#64748b',
                      marginTop: '2px'
                    }}>
                      {formatDate(trade.start_date)}
                      {trade.duration_days > 1 && ` - ${formatDate(trade.end_date)}`}
                      <span style={{ marginLeft: '8px', color: '#94a3b8' }}>
                        ({trade.duration_days} day{trade.duration_days !== 1 ? 's' : ''})
                      </span>
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <div style={{
                    padding: '4px 10px',
                    background: isCompleted ? '#dcfce7' : 
                                trade.status === 'In Progress' ? '#dbeafe' : '#f1f5f9',
                    color: isCompleted ? '#166534' : 
                           trade.status === 'In Progress' ? '#1e40af' : '#64748b',
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    {trade.status}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Milestones Section */}
      {data.milestones?.length > 0 && (
        <div>
          <h4 style={{ 
            margin: '0 0 12px 0', 
            fontSize: '0.875rem', 
            color: '#64748b',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Key Milestones
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {data.milestones.map(milestone => {
              const Icon = MILESTONE_ICONS[milestone.type] || Flag;
              const isCompleted = milestone.is_completed;
              
              return (
                <div
                  key={milestone.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: isCompleted ? '#f0fdf4' : 'white'
                  }}
                  data-testid={`milestone-${milestone.id}`}
                >
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: isCompleted ? '#22c55e' : '#e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {isCompleted ? (
                      <CheckCircle size={18} style={{ color: 'white' }} />
                    ) : (
                      <Icon size={18} style={{ color: '#64748b' }} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: '500', 
                      color: isCompleted ? '#166534' : '#1e293b' 
                    }}>
                      {milestone.title}
                    </div>
                    <div style={{ 
                      fontSize: '0.8125rem', 
                      color: isCompleted ? '#15803d' : '#64748b' 
                    }}>
                      {milestone.start_date ? formatDate(milestone.start_date) : 'Date to be confirmed'}
                    </div>
                  </div>
                  <div style={{
                    padding: '4px 10px',
                    background: isCompleted ? '#dcfce7' : 
                                milestone.status === 'Today' ? '#dbeafe' : '#f1f5f9',
                    color: isCompleted ? '#166534' : 
                           milestone.status === 'Today' ? '#1e40af' : '#64748b',
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    {milestone.status}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Empty State */}
      {(!data.trades || data.trades.length === 0) && (!data.milestones || data.milestones.length === 0) && (
        <div style={{ 
          textAlign: 'center', 
          padding: '32px',
          color: '#64748b'
        }}>
          <Calendar size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
          <div>Timeline will appear here once scheduled.</div>
        </div>
      )}
    </div>
  );
}

// ============ INVOICES SECTION ============

function ClientInvoicesSection({ token, jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/client-portal/view/${token}/job/${jobId}/invoices`);
        if (res.ok) {
          setData(await res.json());
        }
      } catch (err) {
        console.error('Error fetching invoices:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, jobId]);

  if (loading) {
    return (
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '48px', 
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }} data-testid="invoices-section">
      <h3 style={{ 
        margin: '0 0 16px 0', 
        fontSize: '1.125rem', 
        color: '#1e293b' 
      }}>
        Invoices
      </h3>
      
      {/* Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{ 
          padding: '16px', 
          background: '#f8fafc', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e293b' }}>
            {formatCurrency(data.total_invoiced)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Invoiced</div>
        </div>
        <div style={{ 
          padding: '16px', 
          background: '#f0fdf4', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#166534' }}>
            {formatCurrency(data.total_paid)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#15803d' }}>Paid</div>
        </div>
        <div style={{ 
          padding: '16px', 
          background: data.total_outstanding > 0 ? '#fef3c7' : '#f8fafc', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ 
            fontSize: '1.25rem', 
            fontWeight: '700', 
            color: data.total_outstanding > 0 ? '#92400e' : '#64748b' 
          }}>
            {formatCurrency(data.total_outstanding)}
          </div>
          <div style={{ 
            fontSize: '0.75rem', 
            color: data.total_outstanding > 0 ? '#a16207' : '#64748b' 
          }}>
            Outstanding
          </div>
        </div>
      </div>

      {/* Invoice List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {data.invoices.map(invoice => (
          <div
            key={invoice.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              background: invoice.status === 'PAID' ? '#f0fdf4' : 'white'
            }}
            data-testid={`invoice-${invoice.id}`}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Receipt size={20} style={{ 
                color: invoice.status === 'PAID' ? '#22c55e' : '#6366f1' 
              }} />
              <div>
                <div style={{ fontWeight: '500', color: '#1e293b' }}>
                  {invoice.invoice_number}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  {formatDate(invoice.date)}
                  {invoice.due_date && invoice.status !== 'PAID' && (
                    <span> • Due: {formatDate(invoice.due_date)}</span>
                  )}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600', color: '#1e293b' }}>
                {formatCurrency(invoice.total)}
              </div>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: '500',
                color: invoice.status === 'PAID' ? '#166534' : '#64748b'
              }}>
                {invoice.status_display}
              </div>
            </div>
          </div>
        ))}
        
        {data.invoices.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '32px',
            color: '#64748b'
          }}>
            <Receipt size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <div>No invoices yet.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ VARIATIONS SECTION ============

function ClientVariationsSection({ token, jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/client-portal/view/${token}/job/${jobId}/variations`);
        if (res.ok) {
          setData(await res.json());
        }
      } catch (err) {
        console.error('Error fetching variations:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, jobId]);

  if (loading) {
    return (
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '48px', 
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }} data-testid="variations-section">
      <h3 style={{ 
        margin: '0 0 16px 0', 
        fontSize: '1.125rem', 
        color: '#1e293b' 
      }}>
        Approved Variations
      </h3>
      
      {/* Summary */}
      <div style={{
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '16px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>
              Original Quote
            </div>
            <div style={{ fontWeight: '600', color: '#1e293b' }}>
              {formatCurrency(data.original_quote_total)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>
              Variations
            </div>
            <div style={{ 
              fontWeight: '600', 
              color: data.total_variations > 0 ? '#6366f1' : '#64748b' 
            }}>
              {data.total_variations > 0 ? '+' : ''}{formatCurrency(data.total_variations)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>
              Revised Total
            </div>
            <div style={{ fontWeight: '700', color: '#1e293b' }}>
              {formatCurrency(data.revised_total)}
            </div>
          </div>
        </div>
      </div>

      {/* Variations List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {data.variations.map(variation => (
          <div
            key={variation.id}
            style={{
              padding: '16px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px'
            }}
            data-testid={`variation-${variation.id}`}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '8px'
            }}>
              <div style={{ fontWeight: '500', color: '#1e293b' }}>
                {variation.description}
              </div>
              <div style={{ 
                fontWeight: '600', 
                color: variation.amount > 0 ? '#6366f1' : '#dc2626' 
              }}>
                {variation.amount > 0 ? '+' : ''}{formatCurrency(variation.amount)}
              </div>
            </div>
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              {variation.reason}
            </div>
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#94a3b8',
              marginTop: '8px'
            }}>
              Approved: {formatDate(variation.approved_date || variation.proposed_date)}
            </div>
          </div>
        ))}
        
        {data.variations.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '32px',
            color: '#64748b'
          }}>
            <FileText size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <div>No variations have been made to this project.</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientPortalPage;
